import { format } from 'date-fns'

const EMPTY = '—'

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d
}

export function formatDate(
  iso: string | null | undefined,
  fmt = 'dd/MM/yyyy',
): string {
  const d = parseIso(iso)
  return d ? format(d, fmt) : iso || EMPTY
}

export function formatDateTime(
  iso: string | null | undefined,
  fmt = 'dd/MM/yyyy HH:mm',
): string {
  const d = parseIso(iso)
  return d ? format(d, fmt) : iso || EMPTY
}

export function formatTime(
  iso: string | null | undefined,
  fmt = 'HH:mm',
): string {
  const d = parseIso(iso)
  return d ? format(d, fmt) : iso || EMPTY
}

/** Relative time bucket: <24h shows time, <48h shows yesterday label, else short date "MMM d". */
export function formatRelativeTime(
  iso: string | null | undefined,
  yesterdayLabel = 'Yesterday',
): string {
  const d = parseIso(iso)
  if (!d) return iso || EMPTY
  const diffH = (Date.now() - d.getTime()) / 3_600_000
  if (diffH < 24) return formatTime(iso)
  if (diffH < 48) return yesterdayLabel
  return formatDate(iso, 'MMM d')
}

/** "2h 15min" — null-safe. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return EMPTY
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}min`
}

/** Compact — omits zero part: "15min", "2h", or "2h 15min". */
export function formatDurationShort(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return EMPTY
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

/** Long form with days: "3d 2h", "2h 15min", or "15min". Null-safe. */
export function formatDurationLong(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return EMPTY
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}min`
  return `${m}min`
}

/** Zero-padded clock: "04:30". Negative/non-finite → em-dash. */
export function formatHHMM(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return EMPTY
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Locale-grouped number, null-safe. Forwards `Intl.NumberFormatOptions` (fraction digits, etc). */
export function formatNumber(
  n: number | null | undefined,
  opts?: Intl.NumberFormatOptions,
): string {
  if (n == null) return EMPTY
  return n.toLocaleString(undefined, opts)
}

/** Currency value with symbol prefix. EUR → "€1,234". Null-safe. */
export function formatCurrency(
  n: number | null | undefined,
  currency: 'EUR' = 'EUR',
): string {
  if (n == null) return EMPTY
  const symbol = currency === 'EUR' ? '€' : currency
  return `${symbol}${formatNumber(n)}`
}

/** Threshold label: "9h", "4h 30min". */
export function formatMaxLabel(seconds: number): string {
  const h = seconds / 3600
  if (h % 1 === 0) return `${h}h`
  const hours = Math.floor(h)
  const mins = Math.round((h - hours) * 60)
  return `${hours}h ${mins}min`
}
